import { AuthConfig } from 'angular-oauth2-oidc';
import { EnvironmentService } from '@core/services/environment.service';

/**
 * Toggle between refresh strategies:
 *
 * false (current) → Manual refresh: triggers a full re-authentication via
 *                   loadDiscoveryDocumentAndLogin() in a hidden flow.
 *                   Works WITHOUT registering /silent-refresh in Ping.
 *
 * true  (future)  → Library iframe refresh: uses angular-oauth2-oidc's built-in
 *                   silentRefresh() with an iframe pointed at silentRefreshRedirectUri.
 *                   Requires /silent-refresh registered as redirect URI in Ping.
 */
export const USE_LIBRARY_SILENT_REFRESH = false;

// We'll use a function to create the auth config with injected environment configuration
export function createAuthConfig(environmentService: EnvironmentService): AuthConfig {
	const redirectUri = window.location.origin;

	// Check if environment service is properly initialized
	if (!environmentService || !environmentService.isInitialized) {
		console.warn('Environment service not initialized when creating auth config, using fallback values');
		return authConfig;
	}

	// console.log('Creating auth config with environment settings:', {
	//   redirectPingUrl: environmentService.redirectPingUrl,
	//   authClientId: environmentService.authClientId,
	//   loginPingUrl: environmentService.loginPingUrl
	// });

	const loginPingUrl = environmentService.loginPingUrl || 'https://login-dev.witglobal.net';

	return {
		// URL of the Identity Provider
		issuer: loginPingUrl,

		// URL of the SPA to redirect to after login
		redirectUri: environmentService.redirectPingUrl || window.location.origin,

		// The SPA's id in the Identity Provider - Now comes from environment config
		clientId: environmentService.authClientId,

		// The name of the token parameter in the response
		responseType: 'code',

		// The scopes requested from the authorization server
		scope: 'openid profile email',

		// Silent refresh via iframe (only works when USE_LIBRARY_SILENT_REFRESH = true
		// AND the silentRefreshRedirectUri is registered in Ping)
		useSilentRefresh: USE_LIBRARY_SILENT_REFRESH,
		silentRefreshRedirectUri: USE_LIBRARY_SILENT_REFRESH
			? window.location.origin + '/silent-refresh'
			: environmentService.redirectPingUrl || window.location.origin,

		// Show debug information
		showDebugInformation: !environmentService.isProduction,

		// Force redirect to IdP when token expires
		requireHttps: true,
		logoutUrl: `${loginPingUrl}/idp/startSLO.ping?TargetResource=${redirectUri}`,
		// postLogoutRedirectUri: `${redirectUri}`,
		// logoutUrl: `${loginPingUrl}/idp/init_logout.openid`,
		strictDiscoveryDocumentValidation: true,
	};
}

// Export a default authConfig as a backup/fallback
export const authConfig: AuthConfig = {
	// URL of the Identity Provider
	issuer: 'https://login-dev.witglobal.net',

	// The SPA's id in the Identity Provider
	clientId: 'we.online.local',

	// The name of the token parameter in the response
	responseType: 'code',

	// The scopes requested from the authorization server
	scope: 'openid profile email',

	// Enable silentRefresh
	useSilentRefresh: true,
	silentRefreshRedirectUri: window.location.origin + '/silent-refresh',

	// Show debug information
	showDebugInformation: true,

	// Force redirect to IdP when token expires
	requireHttps: true,
	logoutUrl: `https://login-dev.witglobal.net/idp/startSLO.ping?TargetResource=${window.location.origin}`,
	// postLogoutRedirectUri: window.location.origin,
	// logoutUrl: 'https://login-dev.witglobal.net/idp/init_logout.openid',
	loginUrl: 'https://login-dev.witglobal.net/as/authorization.oauth2',
	tokenEndpoint: 'https://login-dev.witglobal.net/as/token.oauth2',
	strictDiscoveryDocumentValidation: true,
};
